"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Command } from "cmdk";
import { Search, ArrowRight, Sparkles, FlaskConical } from "lucide-react";
import { getSuggest } from "@/lib/api";
import type { SuggestItem } from "@/types/api";
import { wikiLink, wikiGroupLink } from "@/lib/url";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SuggestItem[]>([]);
  const router = useRouter();

  // Cmd+K / Ctrl+K 단축키 + 외부 이벤트
  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === "Escape") setOpen(false);
    };
    const openHandler = () => setOpen(true);
    document.addEventListener("keydown", keyHandler);
    window.addEventListener("open-command-palette", openHandler);
    return () => {
      document.removeEventListener("keydown", keyHandler);
      window.removeEventListener("open-command-palette", openHandler);
    };
  }, []);

  // 디바운스 검색 (query가 비어있으면 effect 자체 skip, clear는 onChange에서 처리)
  useEffect(() => {
    if (!query.trim()) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const items = await getSuggest(query.trim());
        if (!cancelled) setResults(items || []);
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const handleQueryChange = useCallback((v: string) => {
    setQuery(v);
    if (!v.trim()) setResults([]);
  }, []);

  const navigate = useCallback(
    (item: SuggestItem) => {
      const href = item.is_group ? wikiGroupLink(item.title) : wikiLink(item.title);
      router.push(href);
      setOpen(false);
      setQuery("");
    },
    [router],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200]">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* 커맨드 팔레트 */}
      <div className="relative mx-auto mt-[15vh] w-[90vw] max-w-lg">
        <Command className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-950">
          {/* 검색 입력 */}
          <div className="flex items-center gap-3 border-b border-gray-100 px-4 dark:border-gray-800">
            <Search className="text-muted-foreground h-4 w-4 shrink-0" />
            <Command.Input
              value={query}
              onValueChange={handleQueryChange}
              placeholder="시술명, 고민 검색..."
              className="placeholder:text-muted-foreground flex-1 border-0 bg-transparent py-4 text-base outline-none"
              autoFocus
            />
            <kbd className="text-muted-foreground hidden rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium sm:inline dark:border-gray-700 dark:bg-gray-900">
              ESC
            </kbd>
          </div>

          {/* 결과 */}
          <Command.List className="max-h-[50vh] overflow-y-auto p-2">
            <Command.Empty className="text-muted-foreground py-8 text-center text-sm">
              {query ? `"${query}" 검색 결과 없음` : "시술명이나 고민을 입력하세요"}
            </Command.Empty>

            {results.length > 0 && (
              <Command.Group heading="시술">
                {results.map((item) => (
                  <Command.Item
                    key={item.title}
                    value={item.title}
                    onSelect={() => navigate(item)}
                    className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors aria-selected:bg-violet-50 dark:aria-selected:bg-violet-950"
                  >
                    <div className="text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 dark:bg-violet-950">
                      {item.is_group ? (
                        <Sparkles className="h-4 w-4" />
                      ) : (
                        <FlaskConical className="h-4 w-4" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{item.title}</span>
                      {item.aliases && (
                        <span className="text-muted-foreground block truncate text-xs">
                          {item.aliases}
                        </span>
                      )}
                    </div>
                    <ArrowRight className="text-muted-foreground h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-aria-selected:opacity-100" />
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* 빈 상태 — 빠른 링크 */}
            {!query && (
              <Command.Group heading="바로 가기">
                <Command.Item
                  onSelect={() => {
                    router.push("/wiki/explore");
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm aria-selected:bg-violet-50"
                >
                  <span className="text-lg">🔍</span>
                  <span>전체 시술 탐색</span>
                </Command.Item>
                <Command.Item
                  onSelect={() => {
                    router.push("/wiki/compare");
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm aria-selected:bg-violet-50"
                >
                  <span className="text-lg">⚖️</span>
                  <span>시술 비교</span>
                </Command.Item>
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}

// 외부에서 열기 위한 이벤트
export function openCommandPalette() {
  window.dispatchEvent(new CustomEvent("open-command-palette"));
}
